import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import * as _ from 'lodash';
import * as moment from 'moment';
import { map, takeUntil, tap } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientAppService } from './../../../../core/services/patient-app.service';
import { PatientCommunicationService } from './../../../../core/services/patient-communication.service';
import { PatientService } from './../../../../core/services/patient.service';
import { WhatsAppChatDialogComponent } from './../../../../shared/components/whatsapp-chat-dialog/whatsapp-chat-dialog.component';
import { CommunicationViewDialogComponent } from './communication-view-dialog.component';

/** One row of the practice's email history (patient-communication.service.ts patientEmails$). */
interface SentEmail {
  id: string;
  subject: string;
  status: string;
  createdAt: string;
  patientAppMirroredAt: string | null;
  patientAppWithdrawnAt: string | null;
}

@Component({
  selector: 'app-patient-communication-list',
  templateUrl: './patient-communication-list.component.html',
  styleUrls: ['./patient-communication-list.component.scss']
})
export class PatientCommunicationListComponent extends BaseComponent implements OnInit {
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  communicationData!: any[] | null;
  patient: any;
  /** Every email sent about this patient (system and practice-generated), for "Remove from patient app". */
  sentEmails: SentEmail[] = [];

  constructor(
    private patientCommunicationService: PatientCommunicationService,
    private patientAppService: PatientAppService,
    private patientService: PatientService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.loadTimelineData();

    this.patientCommunicationService.patientEmails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((emails) => { this.sentEmails = emails || []; });
  }

  /** Only a mirrored, not-yet-withdrawn email can be taken back from the app (patient-app-hardening.md). */
  canWithdrawFromApp(email: SentEmail): boolean {
    // A state unit (tasks/state-mode.md) sends nothing to the patient app, a withdrawal included.
    return Boolean(email.patientAppMirroredAt) && !email.patientAppWithdrawnAt && !isStateModeGroup(this.groupService.group);
  }

  async withdrawMailFromApp(email: SentEmail, ev: MouseEvent): Promise<void> {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.patient?.id || !this.canWithdrawFromApp(email)) return;

    const confirmed = await this.showConfirmationDialog(
      'Remove from patient app?',
      'This removes it from the patient\'s app inbox and timeline. The email itself was already sent and cannot be recalled.',
      [],
      'Cancel',
      'Remove'
    );
    if (!confirmed) return;

    this.patientAppService.withdrawMail(this.patient.id, email.id).subscribe({
      next: () => {
        email.patientAppWithdrawnAt = new Date().toISOString();
        this.showToastMessage('Removed from the patient\'s app.');
      },
      error: () => this.showToastMessage('Could not remove this from the patient\'s app. Please try again.'),
    });
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  loadTimelineData(): void {
    this.patientCommunicationService.patientCommunication$
      .pipe(
        tap((result) => {
          if (result) {
            this.patient = this.patientService.patient;
          }
        }),
        map((items) => {
          // Add processing status flag to each communication item
          const itemsWithProcessing = items?.map((item: any) => ({
            ...item,
            hasProcessingPdf: this.checkHasProcessingPdf(item.media || [])
          }));
          return this.sortCommunicationRecords(itemsWithProcessing);
        }),
        takeUntil(this.destroy$),
      ).subscribe({
        next: (result) => {
          this.communicationData = result;
          //console.log('COMMUNICATION DATA: ', this.communicationData)
        },
        error: (err) => {
          console.error('Error loading communication records:', err);
          this.communicationData = [];
        }
      });
  }

  checkHasProcessingPdf(media: any[]): boolean {
    if (!media || !Array.isArray(media)) {
      return false;
    }
    return media.some((m: any) => 
      m.mediaType === 'application/pdf' && m.processingStatus === 'processing'
    );
  }

  sortCommunicationRecords(consents: any): any[] {

    // Group items by formatted date (no need to format date here since we're just grouping)
    const grouped = _.groupBy(consents, item => item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({ date: key, items: value }))
      // Use the ISO date string directly for sorting
      .orderBy(group => group.date, 'desc')
      .value();

  }

  openWhatsAppDialog(ev: MouseEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.patient?.id) {
      return;
    }

    const dialogRef = this.dialog.open(WhatsAppChatDialogComponent, {
      width: '900px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '90vh',
      data: {
        patientId: this.patient.id,
        patient: this.patient
      }
    });
  }

  openCommunicationDialog(communication: any, ev?: MouseEvent): void {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    this.dialog.open(CommunicationViewDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        communication: communication
      }
    });
  }

  /**
   * Handles a click on a tel: link. Offers to start an AI recording for the call
   * ("Dial + Record") or just dial ("Dial Only"). Recording is triggered via the
   * shared triggerAiRecording subject, which the parent patient-file component
   * listens for to open the AI recording dialog. Opens the dialer in a new context
   * so the page's unload events are not triggered. If a recording is already
   * locked/in progress we skip the prompt and dial directly.
   */
  async dialPhone(event: MouseEvent, phoneNumber?: string): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const number = phoneNumber?.trim();
    if (!number) {
      return;
    }

    const telLink = 'tel:' + number;

    if (this.patient && !this.patientService.isRecordingLocked()) {
      const startRecording = await this.showConfirmationDialog(
        'Start Recording?',
        'You are about to dial this number. Would you like to start an AI-generated recording for the call? Remember to return later to stop the recording once you\'re done.',
        [],
        'Dial Only',
        'Dial + Record'
      );

      if (startRecording) {
        // The parent patient-file component listens for this and opens the AI recording dialog.
        this.patientService.triggerAiRecording.next(true);
        this.showToastMessage('AI recording is starting. The dialer will open in 2 seconds...');
        // Short delay so the recording can start before the dialer takes focus.
        setTimeout(() => this.openTelLink(telLink), 2000);
        return;
      }
    }

    this.openTelLink(telLink);
  }

  private openTelLink(link: string): void {
    // Open via a temporary anchor (with window.open fallback) so the current
    // document's unload/beforeunload handlers are not triggered.
    try {
      const tempLink = document.createElement('a');
      tempLink.href = link;
      tempLink.target = '_blank';
      tempLink.rel = 'noopener noreferrer';
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    } catch (error) {
      console.warn('Failed to open dialer with temporary element method:', error);
      try {
        window.open(link, '_blank');
      } catch (fallbackError) {
        console.error('All methods to open dialer failed:', fallbackError);
        this.showToastMessage('Please manually dial the number: ' + link.replace('tel:', ''));
      }
    }
  }

  /*  loadCommunication(data: any) {
     this.patientCommunicationService.set(data);
     this.router.navigate(['/patients', {
       outlets: {
         patient: ['edit-consent', data.id],
       }
     }]);
   } */

}

